import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {PieChart} from 'echarts/charts';
import {LegendComponent, TooltipComponent} from 'echarts/components';
import * as echarts from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import {EChartsOption, EChartsType} from 'echarts/types/dist/shared';
import {Component, Prop, Ref, Watch} from 'vue-property-decorator';

// Register necessary ECharts components
echarts.use([TooltipComponent, LegendComponent, PieChart, CanvasRenderer]);

interface ZoneCount {
    zoneName: string;
    equipmentCount: number;
}

interface ChartData {
    gasZoneCounts: ZoneCount[];
    dustZoneCounts: ZoneCount[];
}

@Component
export default class MultilayerDonutChartComponent extends VueWrapper {
    @Ref('chartContainer')
    readonly chartContainer!: HTMLDivElement;

    @Prop({type: String, default: '400px'})
    height!: string;

    @Prop({type: String, default: '100%'})
    width!: string;

    @Prop({type: Object, required: true})
    chartData!: ChartData; // Expect the data in the format { gasZoneCounts: [...], dustZoneCounts: [...] }

    private chartInstance: EChartsType | null = null;

    mounted(): void {
        this.initChart();
    }

    beforeDestroy(): void {
        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }
    }

    @Watch('chartData', {deep: true})
    onDataChanged(): void {
        this.updateChart();
    }

    private initChart(): void {
        if (this.chartContainer) {
            this.chartInstance = echarts.init(this.chartContainer);
            this.updateChart();
            window.addEventListener('resize', this.handleResize);
        } else {
            console.error('Chart container not found during init.');
        }
    }

    private updateChart(): void {
        if (!this.chartInstance || !this.chartData) {
            console.warn('Chart instance or data not ready for update.');
            // Attempt to initialize if not already done and container exists
            if (!this.chartInstance && this.chartContainer) {
                this.initChart();
            }
            return; // Exit if still not ready
        }

        const option = this.getChartOptions();
        this.chartInstance.setOption(option, true); // Use true to clear previous options
    }

    private getChartOptions(): EChartsOption {
        // Filter out zones with 0 equipment count if desired, or keep them for completeness
        const gasData = this.chartData.gasZoneCounts
            // .filter(item => item.equipmentCount > 0) // Optional: Uncomment to hide zero-count zones
            .map(item => ({
                value: item.equipmentCount,
                name: item.zoneName,
            }));

        const dustData = this.chartData.dustZoneCounts
            // .filter(item => item.equipmentCount > 0) // Optional: Uncomment to hide zero-count zones
            .map(item => ({
                value: item.equipmentCount,
                name: item.zoneName,
            }));

        // Define colors for consistency or use ECharts defaults
        const gasColors = ['#5470c6', '#91cc75', '#fac858', '#ee6666']; // Example colors
        const dustColors = ['#73c0de', '#3ba272', '#fc8452', '#9a60b4']; // Example colors

        return {
            tooltip: {
                trigger: 'item',
                formatter: '{a} <br/>{b}: {c} ({d}%)',
            },
            legend: {
                // Adjust legend position as needed
                orient: 'horizontal',
                top: 'top',
                left: 'left',
                formatter: (name: string) => {
                    if (this.chartData.gasZoneCounts.some(zone => zone.zoneName === name)) {
                        return `Gas: ${name}`;
                    } else if (this.chartData.dustZoneCounts.some(zone => zone.zoneName === name)) {
                        return `Dust: ${name}`;
                    }
                    return name;
                },
            },
            series: [
                {
                    name: 'Gas Zones',
                    type: 'pie',
                    radius: ['45%', '70%'], // Inner radius, Outer radius for the outer ring
                    center: ['50%', '50%'], // Position the chart center
                    itemStyle: {
                        borderRadius: 5, // Optional: Add rounded corners to segments
                        borderColor: '#fff',
                        borderWidth: 1,
                    },
                    label: {
                        show: false, // Hide labels on the slices themselves
                        position: 'center',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    labelLine: {
                        show: false,
                    },
                    data: gasData,
                    // Assign colors explicitly if needed
                    color: gasColors.slice(0, gasData.length),
                },
                {
                    name: 'Dust Zones',
                    type: 'pie',
                    radius: [0, '35%'], // Inner radius 0, Outer radius for the inner ring
                    center: ['50%', '50%'], // Position the chart center
                    itemStyle: {
                        borderRadius: 5, // Optional: Add rounded corners to segments
                        borderColor: '#fff',
                        borderWidth: 1,
                    },
                    label: {
                        show: false, // Hide labels on the slices themselves
                        position: 'center',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: '16',
                            fontWeight: 'bold',
                        },
                    },
                    labelLine: {
                        show: false,
                    },
                    data: dustData,
                    // Assign colors explicitly if needed
                    color: dustColors.slice(0, dustData.length),
                },
            ],
        };
    }

    private handleResize(): void {
        if (this.chartInstance) {
            this.chartInstance.resize();
        }
    }
}
